/**
 * Per-route SEO: document title, meta description, and robots.
 * Order matters: first matching rule wins (most specific first).
 */

export const SITE_NAME = "TabTrips Extranet";
export const DEFAULT_DESCRIPTION =
  "TabTrips partner extranet: manage properties, rates, reservations, settlements, and campaigns in one place.";

const rules = [
  {
    test: (p) => p === "/login",
    title: "Partner Login",
    description:
      "Sign in to the TabTrips extranet to manage your hotel listings, bookings, rates, and partner tools.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/partner_registrationform",
    title: "Admin — Partner Applications",
    description:
      "Internal admin view for TabTrips partner registration and application management.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/blog/zero-percent-commission-offer",
    title: "Earn Unlimited with TabTrips — 30 Days 0% Commission",
    description:
      "TabTrips limited-time offer: zero commission for the first 30 days on new property signups. Secure bookings, better visibility, partner support.",
    robots: "index, follow",
    ogType: "article",
    ogTitle: "Earn Unlimited with TabTrips — 0% Commission Offer",
    ogDescription:
      "Grow your property with TabTrips — 30 days zero commission for new partner signups.",
    ogImage: "https://tabtrips.com/images/imgoffer.png",
  },
  {
    test: (p) => p === "/blog",
    title: "Partner Blog",
    description:
      "TabTrips Extranet partner blog: launch offers, onboarding tips, and guides to grow hotel bookings and visibility.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/download-app",
    title: "Download TabTrips Extranet App",
    description:
      "Download the TabTrips Extranet mobile app for Android and iOS. Manage rates, inventory, and bookings on the go.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/get-app",
    title: "Get TabTrips Extranet App",
    description:
      "Open the TabTrips Extranet app in Google Play or the App Store based on your device.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/about-us",
    title: "About Us",
    description:
      "Learn about TabTrips and how we help hotels and properties grow bookings through our travel platform.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/support",
    title: "Help & Support",
    description:
      "Get help with the TabTrips extranet: guides, FAQs, and support for partners managing properties and bookings.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/terms-conditions",
    title: "Terms & Conditions",
    description:
      "Terms and conditions for using the TabTrips partner extranet and related hotel listing services.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/privacy-policy",
    title: "Privacy Policy",
    description:
      "How TabTrips collects, uses, and protects your data when you use the partner extranet and our services.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/pay-invoice",
    title: "Pay Invoice",
    description:
      "Secure TabTrips invoice payment page for partners. View and pay outstanding commission or subscription invoices.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/dashboard",
    title: "Dashboard",
    description:
      "TabTrips extranet dashboard: performance overview, key metrics, and quick access to your property tools.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/settlements",
    title: "Settlements",
    description:
      "Review booking settlements, transfer status, and payouts for your TabTrips-listed properties.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/performance",
    title: "Performance",
    description:
      "Analyse campaign and property performance: impressions, clicks, and results across your TabTrips listings.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/room-types",
    title: "Room Types",
    description:
      "Configure room types, categories, and inventory structure for your property on TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/invoice",
    title: "Invoices",
    description:
      "View and manage TabTrips invoices, payment status, and billing history for your property account.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/property-images" || p === "/hotel-images",
    title: "Property Images",
    description:
      "Upload and organise main, reception, and room images for your TabTrips property listing.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/reservations"),
    title: "Reservations",
    description:
      "Manage guest reservations, booking details, check-in status, and settlement information on TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/property"),
    title: "Property Details",
    description:
      "Edit property profile, amenities, policies, and listing information for TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/reviews"),
    title: "Guest Reviews",
    description:
      "Read and respond to guest reviews for your TabTrips-listed property.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/promotions"),
    title: "Promotions",
    description:
      "Create and manage promotional offers and deals for your TabTrips property.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/property-boost"),
    title: "Property Boost",
    description:
      "Boost visibility and commission settings to improve your property’s presence on TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/campaign/running"),
    title: "Running campaigns",
    description:
      "View sponsored listing status, wallet balance, and reported clicks and impressions for your TabTrips property.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/campaign"),
    title: "Sponsored listing",
    description:
      "Create a sponsored listing campaign: wallet, quotes, checkout, and launch for your TabTrips property.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/analytics"),
    title: "Analytics",
    description:
      "Property analytics and reporting: bookings, revenue insights, and trends in the TabTrips extranet.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/settings"),
    title: "Settings",
    description:
      "Account and property settings: profile, security, notifications, and preferences on TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/add-property"),
    title: "Add Property",
    description:
      "Add a new hotel or property to TabTrips: details, location, and setup wizard for partners.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/listingbyadmin" || p.startsWith("/listingbyadmin/"),
    title: "Admin Listing",
    description: "Staff listing flow for creating a TabTrips host account.",
    robots: "noindex, nofollow",
  },
  {
    test: (p) => p.startsWith("/listing"),
    title: "List Your Property",
    description:
      "Create your TabTrips partner account and list your property: account setup, property details, location, amenities, room setup, and review.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/add-image"),
    title: "Add Property Images",
    description:
      "Upload property photos and gallery images for your TabTrips listing during onboarding.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/rates-inventory"),
    title: "Rates & Inventory",
    description:
      "Set room rates, availability, and inventory by date for your property on TabTrips.",
    robots: "index, follow",
  },
  {
    test: (p) => p.startsWith("/room-rates-calendar"),
    title: "Room Rates Calendar",
    description:
      "Calendar view to bulk-update room counts, rates, and day-wise pricing for TabTrips inventory.",
    robots: "index, follow",
  },
  {
    test: (p) => p === "/",
    title: "My Properties",
    description:
      "Overview of your TabTrips properties: switch hotels, open tools, and manage listings from one place.",
    robots: "index, follow",
  },
];

/**
 * @param {string} pathname - from useLocation().pathname (no query string)
 */
export function getSeoForPath(pathname) {
  let path = pathname || "/";
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  for (const rule of rules) {
    if (rule.test(path)) {
      return {
        title: rule.title,
        description: rule.description,
        robots: rule.robots || "index, follow",
      };
    }
  }
  return {
    title: "Page Not Found",
    description: DEFAULT_DESCRIPTION,
    robots: "index, follow",
  };
}

